/**
 * Seed's reporting fiscal year: 1 September → 31 August.
 *
 * FY26 means 1 Sep 2025 - 31 Aug 2026 - named for the year it ENDS in, which is
 * how the finance team refers to it.
 *
 * Deliberately NOT the same as `fiscalYearOf()` in the backend's
 * perdiemlodging_claim/controller/conusRates.mjs. That one is the US federal
 * year (1 Oct - 30 Sep) used to pick GSA per diem rate tables. The two differ by
 * a month, and reusing the wrong one would silently shift every reporting period
 * - so they stay separate, with this comment as the reason.
 *
 * Every boundary is built from LOCAL date components. Never construct these with
 * toISOString(): local midnight anywhere ahead of UTC converts to the previous
 * day, which is exactly the class of bug that made 1 April price as 31 March in
 * the per diem lookup.
 */

/** The fiscal year a given date falls in. Sep-Dec belong to the NEXT FY. */
export const seedFiscalYear = (value = new Date()) => {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return new Date().getFullYear();
  // getMonth() is 0-based: 8 = September.
  return d.getMonth() >= 8 ? d.getFullYear() + 1 : d.getFullYear();
};

/**
 * The inclusive window for a fiscal year, as Date objects in local time.
 * `to` is the last instant of 31 August so a decision recorded that evening is
 * still inside the year it belongs to.
 */
export const fiscalYearRange = (fy) => ({
  from: new Date(fy - 1, 8, 1, 0, 0, 0, 0),
  to: new Date(fy, 7, 31, 23, 59, 59, 999),
});

/** The inclusive window for a calendar month within a fiscal year. */
export const fiscalMonthRange = (fy, month) => {
  // Sep-Dec (9-12) sit in the calendar year before the one the FY is named for.
  const calendarYear = month >= 9 ? fy - 1 : fy;
  return {
    from: new Date(calendarYear, month - 1, 1, 0, 0, 0, 0),
    // Day 0 of the next month is the last day of this one, leap years included.
    to: new Date(calendarYear, month, 0, 23, 59, 59, 999),
  };
};

/** "FY26 (Sep 2025 - Aug 2026)" */
export const fiscalYearLabel = (fy) =>
  `FY${String(fy).slice(-2)} (Sep ${fy - 1} - Aug ${fy})`;

/** Short form for a dropdown option. */
export const fiscalYearShortLabel = (fy) => `FY${String(fy).slice(-2)}`;

/** The months of a fiscal year in the order they occur, Sep first. */
export const FISCAL_MONTHS = [
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
];

/** A run of selectable fiscal years, newest first. */
export const fiscalYearOptions = (count = 5, now = new Date()) => {
  const current = seedFiscalYear(now);
  return Array.from({ length: count }, (_, i) => current - i);
};

const fiscalYear = {
  seedFiscalYear,
  fiscalYearRange,
  fiscalMonthRange,
  fiscalYearLabel,
  fiscalYearShortLabel,
  fiscalYearOptions,
  FISCAL_MONTHS,
};

export default fiscalYear;
